import { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JoditEditor from 'jodit-react';
import { ArrowLeft, Save, Image as ImageIcon, X } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth.store';
import { useBlogPost, useUpdateBlogPost, useBlogCategories, useUploadImage } from '@/hooks/useBlog';

export function UpdateBlog() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: post, isLoading } = useBlogPost(id || '');
  const updatePost = useUpdateBlogPost();
  const { data: categories } = useBlogCategories();
  const uploadImage = useUploadImage();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (post && !initialized) {
      setTitle(post.title);
      setContent(post.content);
      setExcerpt(post.excerpt);
      setCoverImage(post.coverImage || '');
      setCategoryId(post.category?.id || '');
      setTagsInput(post.tags.join(', '));
      setStatus(post.status === 'published' ? 'published' : 'draft');
      setInitialized(true);
    }
  }, [post, initialized]);

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/blog" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="mx-auto max-w-4xl animate-pulse space-y-6">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-10 w-full bg-muted rounded" />
            <div className="h-20 w-full bg-muted rounded" />
            <div className="h-96 w-full bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadImage.mutateAsync(file);
      setCoverImage(result.url);
    } catch {
      // Upload failed
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !title.trim() || !content.trim()) return;

    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await updatePost.mutateAsync({
        id,
        data: {
          title,
          content,
          excerpt,
          coverImage: coverImage || undefined,
          categoryId,
          tags,
          status,
        },
      });
      navigate('/blog/my-posts');
    } catch {
      // Update failed
    }
  };

  const joditConfig = {
    readonly: false,
    height: 500,
    toolbarAdaptive: false,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'brush', '|',
      'image', 'link', 'table', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize', 'source',
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>

          <h1 className="text-2xl font-bold mb-8">{t('blogPage.editPost')}</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.postTitle')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('blogPage.postTitlePlaceholder')}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.excerpt')}</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder={t('blogPage.excerptPlaceholder')}
                rows={2}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.coverImage')}</label>
              {coverImage ? (
                <div className="relative rounded-lg overflow-hidden max-w-md">
                  <img src={coverImage} alt="Cover" className="w-full aspect-video object-cover" />
                  <button
                    type="button"
                    onClick={() => setCoverImage('')}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-sm text-muted-foreground hover:border-emerald-500 hover:text-emerald-600 transition-colors justify-center">
                  <ImageIcon className="h-5 w-5" />
                  {t('blogPage.uploadCoverImage')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.category')}</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">{t('blogPage.selectCategory')}</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.tags')}</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder={t('blogPage.tagsPlaceholder')}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">{t('blogPage.content')}</label>
              <JoditEditor
                value={content}
                config={joditConfig}
                onBlur={(newContent) => setContent(newContent)}
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">{t('blogPage.status')}:</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                  className="rounded-lg border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="draft">{t('blogPage.draft')}</option>
                  <option value="published">{t('blogPage.published')}</option>
                </select>
              </div>
              <Button
                type="submit"
                disabled={updatePost.isPending || !title.trim()}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {updatePost.isPending ? t('common.loading') : t('blogPage.saveChanges')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
